<div class="parent-learn">
  <header class="parent-learn__toolbar">
    <button
      type="button"
      class="parent-learn__nav"
      (click)="goBack()"
      [attr.aria-label]="'app.parentLearning.back' | translate"
    >
      <span class="parent-learn__nav-icon" aria-hidden="true"></span>
      @if (detail(); as course) {
        <h1 class="parent-learn__nav-title">{{ course.courseName }}</h1>
      }
    </button>
  </header>

  @if (isLoading()) {
    <div class="parent-learn__loading" role="status">
      {{ 'app.parentLearning.loading' | translate }}
    </div>
  } @else if (loadError()) {
    <div class="parent-learn__error" role="alert">
      {{ loadError() }}
    </div>
  } @else {
    @if (detail(); as course) {
    @if (isLessonActive() && isMobile()) {
      <app-parent-learn-progress
        [compact]="true"
        [percent]="course.completionPercent"
        [completedCount]="course.completedTaskCount"
        [totalCount]="course.taskCount"
        [totalPoints]="course.totalPoints"
      />
    }

    <div class="parent-learn__intro" [class.parent-learn__intro--collapsed]="isLessonActive() && isMobile()">
      @if (course.courseImageUrl || course.courseDescription) {
        <section
          class="parent-learn__hero"
          [class.parent-learn__hero--cover-only]="course.courseImageUrl && !course.courseDescription"
        >
          @if (course.courseImageUrl) {
            <img
              class="parent-learn__hero-image"
              [src]="course.courseImageUrl"
              [alt]="course.courseName"
            />
          }
          @if (course.courseDescription) {
            <div class="parent-learn__hero-copy">
              <p
                class="parent-learn__description"
                [class.parent-learn__description--clamped]="isMobile() && !descriptionExpanded()"
              >
                {{ course.courseDescription }}
              </p>
              @if (isMobile() && isDescriptionLong(course.courseDescription)) {
                <button
                  type="button"
                  class="parent-learn__description-toggle"
                  [attr.aria-expanded]="descriptionExpanded()"
                  (click)="toggleDescription()"
                >
                  {{
                    descriptionExpanded()
                      ? ('app.parentLearning.description.showLess' | translate)
                      : ('app.parentLearning.description.showMore' | translate)
                  }}
                </button>
              }
            </div>
          }
        </section>
      }

      <app-parent-learn-progress
        [percent]="course.completionPercent"
        [completedCount]="course.completedTaskCount"
        [totalCount]="course.taskCount"
        [totalPoints]="course.totalPoints"
      />

      <app-parent-learn-enroll-card
        class="parent-learn__enroll"
        [joined]="isJoined()"
        [completed]="isCourseCompleted()"
        [joinLoading]="joinMutation.isPending()"
        [leaveLoading]="leaveMutation.isPending()"
        (join)="onJoin()"
        (leave)="onLeave()"
      />
    </div>

    @if (phase() === 'course-done') {
      <section class="parent-learn__celebration" aria-live="polite">
        <h2>{{ 'app.parentLearning.courseDone.title' | translate }}</h2>
        <p>{{ 'app.parentLearning.courseDone.text' | translate }}</p>
        <div class="parent-learn__rating">
          <app-course-rating
            [userRating]="userRating()"
            [disabled]="rateMutation.isPending()"
            (onRateCourse)="onRateCourse($event)"
          />
        </div>
        <app-parent-button variant="primary" (clicked)="goBack()">
          {{ 'app.parentLearning.courseDone.cta' | translate }}
        </app-parent-button>
      </section>
    }

    <div class="parent-learn__layout" [class.parent-learn__layout--active]="isLessonActive()">
      <aside class="parent-learn__sidebar">
        <app-parent-learn-lesson-list
          [lessons]="lessons()"
          [selectedLessonId]="selectedLessonId()"
          [newlyUnlockedId]="newlyUnlockedId()"
          [disabled]="!isJoined()"
          (lessonSelect)="onLessonSelect($event)"
        />
      </aside>

      <main class="parent-learn__main">
        @if (!isJoined()) {
          <section class="parent-learn__gate">
            <p>{{ 'app.parentLearning.gate.joinToStart' | translate }}</p>
          </section>
        } @else if (phase() === 'overview') {
          <section class="parent-learn__overview">
            <h2>{{ 'app.parentLearning.overview.title' | translate }}</h2>
            <p>{{ 'app.parentLearning.overview.text' | translate }}</p>
            @if (continueLesson(); as lesson) {
              <app-parent-button variant="primary" (clicked)="onContinueLearning()">
                {{
                  'app.parentLearning.overview.startLesson'
                    | translate: { title: lesson.title }
                }}
              </app-parent-button>
            }
          </section>
        } @else {
          <section #learnStage class="parent-learn__stage" aria-live="polite">
            @if (selectedLesson(); as lesson) {
              <header class="parent-learn__stage-header">
                <div>
                  <h2>{{ lesson.title }}</h2>
                  @if (reviewMode()) {
                    <p class="parent-learn__review-label">
                      {{ 'app.parentLearning.review.label' | translate }}
                    </p>
                  }
                </div>
                @if (lesson.isCompleted) {
                  <span class="parent-learn__stage-badge parent-learn__stage-badge--done">
                    {{ 'app.parentLearning.lessons.completed' | translate }}
                  </span>
                }
              </header>
            }

            @if (taskQuery.isLoading() || (phase() === 'quiz' && quizQuery.isLoading())) {
              <app-parent-learn-skeleton
                [variant]="phase() === 'quiz' ? 'quiz' : 'video'"
              />
            } @else if (taskQuery.error()) {
              <p class="parent-learn__stage-error" role="alert">
                {{ taskErrorMessage() }}
              </p>
            } @else if (actionError()) {
              <p class="parent-learn__stage-error" role="alert">
                {{ actionError() }}
              </p>
            } @else {
              @switch (phase()) {
                @case ('watch') {
                  @if (hasVideo()) {
                    @if (showStickyVideo()) {
                      <div
                        #stickySentinel
                        class="parent-learn__sticky-sentinel"
                        aria-hidden="true"
                      ></div>
                    }
                    <div class="parent-learn__video-sentinel">
                      @if (videoDocked() && dockedVideoHeight()) {
                        <div
                          class="parent-learn__video-spacer"
                          [style.height.px]="dockedVideoHeight()"
                          aria-hidden="true"
                        ></div>
                      }
                      <div
                        class="parent-learn__video"
                        [class.parent-learn__video--offscreen]="videoDocked()"
                        role="region"
                        [attr.aria-label]="'app.parentLearning.sticky.playerLabel' | translate"
                      >
                        <app-course-video
                          [videoUrl]="videoUrl()"
                          [posterUrl]="posterUrl()"
                          (videoEnded)="onVideoEnded()"
                        />
                      </div>
                    </div>
                    @if (!reviewMode() && !videoWatched()) {
                      <p class="parent-learn__stage-hint">
                        {{ 'app.parentLearning.stage.watchHint' | translate }}
                      </p>
                    } @else if (hasQuiz()) {
                      <app-parent-button
                        variant="primary"
                        (clicked)="startQuiz()"
                      >
                        {{
                          reviewMode()
                            ? ('app.parentLearning.review.quiz' | translate)
                            : ('app.parentLearning.stage.startQuiz' | translate)
                        }}
                      </app-parent-button>
                    }
                  } @else if (hasQuiz()) {
                    <app-parent-learn-quiz
                      [quizId]="quizId()"
                      [taskId]="selectedLessonId()"
                      [reviewMode]="reviewMode()"
                      [description]="activeTaskDescription()"
                      (answerIncorrect)="onQuizIncorrect()"
                      (completed)="onQuizCompleted($event)"
                    />
                    @if (showRewatch()) {
                      <app-parent-button variant="ghost" (clicked)="onRewatchVideo()">
                        {{ 'app.parentLearning.stage.rewatchVideo' | translate }}
                      </app-parent-button>
                    }
                  } @else {
                    <p class="parent-learn__stage-hint">
                      {{ 'app.parentLearning.stage.noContent' | translate }}
                    </p>
                    @if (!reviewMode()) {
                      <app-parent-button
                        variant="primary"
                        (clicked)="completeLessonWithoutQuiz()"
                      >
                        {{ 'app.parentLearning.stage.markComplete' | translate }}
                      </app-parent-button>
                    }
                  }
                }
                @case ('quiz') {
                  @if (canStartQuiz()) {
                    <app-parent-learn-quiz
                      [quizId]="quizId()"
                      [taskId]="selectedLessonId()"
                      [reviewMode]="reviewMode()"
                      [description]="activeTaskDescription()"
                      (answerIncorrect)="onQuizIncorrect()"
                      (completed)="onQuizCompleted($event)"
                    />
                    @if (quizResult(); as result) {
                      <p class="parent-learn__review-score" aria-live="polite">
                        @if (result.isPerfect) {
                          {{ 'app.components.quiz.perfectScore' | translate }}
                        } @else {
                          {{
                            'app.components.quiz.passedWithScore'
                              | translate
                                : { score: result.correctCount, total: result.total }
                          }}
                        }
                      </p>
                    }
                    @if (showRewatch()) {
                      <app-parent-button variant="ghost" (clicked)="onRewatchVideo()">
                        {{ 'app.parentLearning.stage.rewatchVideo' | translate }}
                      </app-parent-button>
                    }
                  } @else {
                    <p class="parent-learn__stage-hint">
                      {{ 'app.parentLearning.stage.quizLocked' | translate }}
                    </p>
                    <app-parent-button variant="primary" (clicked)="backToVideo()">
                      {{ 'app.parentLearning.stage.backToVideo' | translate }}
                    </app-parent-button>
                  }
                }
                @case ('lesson-done') {
                  <section class="parent-learn__lesson-done">
                    @if (quizResult(); as result) {
                      <h3>
                        @if (result.isPerfect) {
                          {{ 'app.components.quiz.perfectScore' | translate }}
                        } @else {
                          {{
                            'app.components.quiz.passedWithScore'
                              | translate
                                : { score: result.correctCount, total: result.total }
                          }}
                        }
                      </h3>
                    } @else {
                      <h3>{{ 'app.parentLearning.lessonDone.title' | translate }}</h3>
                    }
                    <p>{{ 'app.parentLearning.lessonDone.text' | translate }}</p>
                    <div class="parent-learn__lesson-done-actions">
                      @if (nextLesson(); as next) {
                        <app-parent-button variant="primary" (clicked)="onNextLesson()">
                          {{
                            'app.parentLearning.lessonDone.next'
                              | translate: { title: next.title }
                          }}
                        </app-parent-button>
                      }
                      <app-parent-button variant="ghost" (clicked)="onBackToOverview()">
                        {{ 'app.parentLearning.lessonDone.overview' | translate }}
                      </app-parent-button>
                    </div>
                  </section>
                }
              }
            }
          </section>
        }
      </main>
    </div>

    @if (videoDocked() && selectedLesson(); as lesson) {
      <div
        #nowPlayingDock
        class="parent-learn__now-playing-dock"
        role="region"
        [attr.aria-label]="'app.parentLearning.sticky.nowPlaying' | translate"
      >
        <app-parent-learn-sticky-player
          [lessonTitle]="lesson.title"
          (expand)="scrollToInlineVideo()"
        />
      </div>
    }
    <div #dockAnchor class="parent-learn__dock-anchor" aria-hidden="true"></div>
    } @else {
      <div class="parent-learn__error" role="alert">
        {{ 'app.parentLearning.notFound' | translate }}
      </div>
    }
  }
</div>
